import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/hooks/useAppStore';
import { useAppConfig } from '@/hooks/useAppConfig';
import AgreementSheet from '@/components/feature/AgreementSheet';
import UserHeader from './components/UserHeader';
import HistoryGrid from './components/HistoryGrid';
import PointsModal from './components/PointsModal';
import { getHistory } from '@/api/generation';
import { track } from '@/utils/tracker';

type TabType = 'menu' | 'history' | 'favorites';

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, authInitialized } = useAppStore();
  const config = useAppConfig();
  const { t } = useTranslation();
  const requestedTab = searchParams.get('tab');
  const cameFromGenerate = searchParams.get('from') === 'generate';
  const initialTab: TabType = requestedTab === 'history' || requestedTab === 'favorites'
    ? requestedTab
    : 'menu';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [genCount, setGenCount] = useState<number | undefined>(undefined);
  const [favCount, setFavCount] = useState(0);
  const [activeAgreement, setActiveAgreement] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    track.pageView('profile');
    getHistory(1, 1).then(res => setGenCount(res.total)).catch(() => {});
  }, [user]);
  useEffect(() => {
    const nextTab = searchParams.get('tab');
    setActiveTab(nextTab === 'history' || nextTab === 'favorites' ? nextTab : 'menu');
  }, [searchParams]);

  const selectTab = (tab: TabType) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'menu') next.delete('tab');
    else next.set('tab', tab);
    if (tab !== 'history') {
      next.delete('status');
      next.delete('model');
      next.delete('period');
      next.delete('order');
      next.delete('gen_id');
    }
    setSearchParams(next, { replace: true });
  };
  const [contactModal, setContactModal] = useState(false);

  if (!authInitialized) {
    return (
      <div className="min-h-screen font-nunito flex flex-col" style={{ background: '#F8F7FF' }}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen font-nunito flex flex-col relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #FFF0F6 0%, #F5EEFF 50%, #F0F4FF 100%)' }}>
        {/* 背景装饰 */}
        <div className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #FF6B9D22 0%, transparent 70%)', transform: 'translate(20%, -20%)' }} />
        <div className="absolute bottom-20 left-0 w-40 h-40 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, #A855F718 0%, transparent 70%)', transform: 'translate(-30%, 0)' }} />

        <div className="max-w-md mx-auto w-full flex-1 flex flex-col items-center justify-center px-5">
          <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6"
            style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}>
            <i className="ri-user-line text-white text-4xl"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t('profile_loginTitle')}</h2>
          <p className="text-sm text-gray-400 mb-8 text-center leading-relaxed" style={{ whiteSpace: 'pre-line' }}>
            {t('profile_loginDesc')}
          </p>
          <button
            onClick={() => navigate('/login?redirect=/profile')}
            className="w-full py-4 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}
          >
            {t('profile_loginBtn')}
          </button>
        </div>
      </div>
    );
  }

  const contactEmail = config?.contact?.email?.trim() || 'paidaxing1121@gmail.com';
  const contactPhone = config?.contact?.phone?.trim() || '';
  const supportHours = config?.contact?.support_hours?.trim() || t('profile_supportHours');

  const menuItems = [
    {
      icon: 'ri-flashlight-line',
      label: t('profile_points'),
      desc: `${t('profile_currentPoints')} ${user.points} ${t('gen_points')}`,
      color: '#FF6B9D',
      action: () => setShowPointsModal(true),
    },
    {
      icon: 'ri-vip-crown-line',
      label: t('profile_membership'),
      desc: user.isVip ? t('profile_vipMember') : t('profile_membershipDesc'),
      color: '#A855F7',
      action: () => navigate('/membership'),
    },
    {
      icon: 'ri-settings-3-line',
      label: t('profile_settings'),
      desc: '',
      color: '#6366F1',
      action: () => navigate('/settings'),
    },
    {
      icon: 'ri-customer-service-2-line',
      label: t('profile_contact'),
      desc: '',
      color: '#EC4899',
      action: () => setContactModal(true),
    },
  ];

  return (
    <div className="min-h-screen font-nunito flex flex-col" style={{ background: '#F8F7FF' }}>
      <div className="max-w-md mx-auto w-full min-h-screen flex flex-col">

        {/* 用户头部 */}
        <UserHeader user={user} onPointsClick={() => setShowPointsModal(true)} generationCount={genCount} />

        {/* Tab 切换 */}
        <div className="px-4 pt-3 pb-2 bg-white/60 backdrop-blur-sm sticky top-0 z-30">
          {activeTab === 'history' && cameFromGenerate && (
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                data-testid="history-continue-generation"
                onClick={() => navigate('/generate')}
                className="flex items-center gap-1 rounded-full bg-purple-50 px-3 py-1.5 text-[11px] font-semibold text-purple-600 active:bg-purple-100"
              >
                <i className="ri-magic-line" />
                {t('profile_continueCreating')}
              </button>
            </div>
          )}
          <div className="flex items-center justify-between gap-1">
            {[
              { key: 'menu' as TabType, label: t('profile_menu'), icon: 'ri-user-3-line', badge: undefined as number | undefined },
              { key: 'history' as TabType, label: t('profile_history'), icon: 'ri-image-2-line', badge: undefined as number | undefined },
              { key: 'favorites' as TabType, label: t('profile_favorites'), icon: 'ri-star-line', badge: favCount > 0 ? favCount : undefined },
            ].map((tab) => (
              <button
                key={tab.key}
                data-testid={`profile-tab-${tab.key}`}
                aria-label={`profile-tab-${tab.key}`}
                onClick={() => selectTab(tab.key)}
                className={`relative flex min-w-0 items-center justify-center gap-1 px-1.5 py-2 rounded-full transition-all flex-1 cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-gradient-to-r from-[#FF6B9D] to-[#A855F7] text-white shadow-sm'
                    : 'bg-gray-100/80 text-gray-500 hover:bg-gray-200/80'
                }`}
              >
                <i className={`${tab.icon} text-xs flex-shrink-0`}></i>
                <span className="min-w-0 truncate text-[11px] font-semibold">{tab.label}</span>
                {tab.badge !== undefined && activeTab !== tab.key && (
                  <span className="ml-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-pink-500 text-white text-[9px] font-bold leading-none">
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab 内容 */}
        <div
          className="flex-1 overflow-y-auto overscroll-y-none"
          style={{ paddingBottom: 'var(--app-bottom-nav-offset)' }}
        >
          {activeTab === 'menu' && (
            <div className="px-5 pt-4">
              {/* 功能列表 */}
              <div className="bg-white rounded-3xl overflow-hidden">
                {menuItems.map((item, idx) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    aria-label={item.label === t('profile_settings') ? 'profile-settings' : undefined}
                    className={`w-full flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${idx < menuItems.length - 1 ? 'border-b border-gray-50' : ''}`}
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: `${item.color}15` }}>
                      <i className={`${item.icon} text-base`} style={{ color: item.color }}></i>
                    </div>
                    <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-gray-800">{item.label}</span>
                    {item.desc && <span className="max-w-[45%] truncate text-xs text-gray-400">{item.desc}</span>}
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-gray-300 text-base"></i>
                    </div>
                  </button>
                ))}
              </div>

              {/* 版本号 */}
              <p className="text-center text-xs text-gray-300 mt-6">{t('profile_version')}</p>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="pt-4">
              <HistoryGrid onCountLoaded={setGenCount} />
            </div>
          )}

          {activeTab === 'favorites' && (
            <div className="pt-4">
              <HistoryGrid favoritedOnly={true} onCountLoaded={setFavCount} />
            </div>
          )}
        </div>

      </div>

      {/* 积分弹层 */}
      <PointsModal
        isOpen={showPointsModal}
        onClose={() => setShowPointsModal(false)}
        currentPoints={user.points}
      />

      {/* 协议弹层 */}
      {activeAgreement && (
        <AgreementSheet
          agreement={config?.agreements?.[activeAgreement]}
          onClose={() => setActiveAgreement(null)}
        />
      )}

      {/* 联系客服弹窗 */}
      {contactModal && (
        <div className="fixed inset-0 z-[100] flex items-end bg-black/40">
          <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl p-6 animate-slide-up"
               style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">{t('profile_contactTitle')}</h3>
              <button onClick={() => setContactModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-4 rounded-2xl"
                style={{ background: 'linear-gradient(135deg, #FFF0F6, #F5EEFF)' }}>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
                  <i className="ri-mail-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{t('profile_emailService')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{contactEmail}</p>
                </div>
              </div>
              {contactPhone && (
                <div className="flex items-center gap-4 p-4 rounded-2xl"
                  style={{ background: 'linear-gradient(135deg, #F0FFF4, #ECFDF5)' }}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: '#10B981' }}>
                    <i className="ri-phone-line text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{t('profile_phoneService')}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{contactPhone}</p>
                  </div>
                </div>
              )}
              <div className="pt-2 text-center">
                <p className="text-xs text-gray-400">{supportHours}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
